import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useVendorSubscriptionFeatures } from '../../services/subscriptionService';
import {
  Check,
  X,
  BarChart3,
  Palette,
  Headphones,
  Clock,
  MapPin,
  ArrowRight,
  Package,
  ListChecks,
  Loader2,
} from 'lucide-react';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const FEATURES = [
  { key: 'feature_analytics', label: 'Advanced Analytics', icon: BarChart3, href: '/vendor/advanced-analytics' },
  { key: 'feature_custom_branding', label: 'Custom Branding', icon: Palette, href: '/vendor/branding' },
  { key: 'feature_priority_support', label: 'Priority Support', icon: Headphones, href: '/vendor/support' },
  { key: 'feature_advanced_booking', label: 'Advanced Booking', icon: Clock, href: '/vendor/bookings' },
  { key: 'feature_multi_location', label: 'Multi-Location', icon: MapPin, href: '/vendor/locations' },
];

const VendorPlanFeatures = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hasActiveSubscription, isChecking, currentSubscription, isLoading: loading } =
    useVendorSubscriptionFeatures();

  useEffect(() => {
    if (user?.user_type !== 'vendor') {
      navigate('/');
      return;
    }
    if (!hasActiveSubscription) {
      navigate('/vendor/subscription', { replace: true });
    }
  }, [user?.user_type, hasActiveSubscription, navigate]);

  const subscriptionDetails = currentSubscription;
  const features = subscriptionDetails?.features || {};
  const hasFeature = (key) => !!features[key];

  if (isChecking || loading) {
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm py-20 text-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading plan features…</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-2xl">
      <VendorPageHeader
        title="Your Plan Features"
        subtitle={
          subscriptionDetails?.plan_name
            ? `${subscriptionDetails.plan_name} — features included in your subscription.`
            : 'See what your current plan includes.'
        }
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.planFeatures.title}
        steps={VENDOR_PAGE_GUIDES.planFeatures.steps}
        icon={ListChecks}
      />

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="divide-y divide-gray-200 dark:divide-gray-800">
          {FEATURES.map((feature) => {
            const Icon = feature.icon;
            const included = hasFeature(feature.key);
            const content = (
              <>
                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                  {included ? (
                    <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  ) : (
                    <X className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  )}
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="text-sm sm:text-base font-medium text-gray-900 dark:text-gray-50 truncate">
                    {feature.label}
                  </span>
                </div>
                {included ? (
                  <ArrowRight className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                ) : (
                  <Link
                    to="/vendor/subscription"
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs sm:text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center gap-1 shrink-0"
                  >
                    Upgrade plan
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
              </>
            );
            return included ? (
              <Link
                key={feature.key}
                to={feature.href}
                className="flex items-center justify-between p-4 sm:p-5 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
              >
                {content}
              </Link>
            ) : (
              <div
                key={feature.key}
                className="flex items-center justify-between p-4 sm:p-5 transition-colors cursor-default opacity-75"
              >
                {content}
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-gray-800 dark:border-gray-700 bg-gradient-to-br from-gray-900 to-black p-4 sm:p-6 text-white shadow-sm">
        <div className="flex items-center gap-3 mb-2">
          <Package className="w-6 h-6" />
          <h2 className="text-lg font-semibold">Need more features?</h2>
        </div>
        <p className="text-sm text-gray-300 mb-4">
          Upgrade your plan to unlock Custom Branding, Priority Support, Advanced Booking, and
          Multi-Location.
        </p>
        <Link
          to="/vendor/subscription"
          className="inline-flex items-center gap-2 px-4 py-2 bg-white text-black !rounded-xl font-medium text-sm hover:bg-gray-100 transition-colors"
        >
          View plans & upgrade
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};

export default VendorPlanFeatures;
